import { Controller } from "@hotwired/stimulus"
import { Chart, RadarController, BarController, DoughnutController, PolarAreaController, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

// Register Chart.js components
Chart.register(
  RadarController,
  BarController,
  DoughnutController,
  PolarAreaController,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  ChartDataLabels
)

// Leadership Style Visualization Controller
export default class extends Controller {
  static targets = [
    "styleComparison",
    "effectivenessChart",
    "situationalAdaptability",
    "teamCompatibility"
  ]

  static values = {
    styleData: Object,
    comparisonData: Object,
    effectivenessData: Object,
    adaptabilityData: Object,
    teamData: Object
  }

  connect() {
    this.initializeCharts()
  }

  initializeCharts() {
    if (this.hasStyleComparisonTarget && this.hasComparisonDataValue) {
      this.initStyleComparisonChart()
    }

    if (this.hasEffectivenessChartTarget && this.hasEffectivenessDataValue) {
      this.initEffectivenessChart()
    }

    if (this.hasSituationalAdaptabilityTarget && this.hasAdaptabilityDataValue) {
      this.initSituationalAdaptabilityChart()
    }

    if (this.hasTeamCompatibilityTarget && this.hasTeamDataValue) {
      this.initTeamCompatibilityChart()
    }
  }

  // Style Comparison Radar Chart (compare to team averages or ideal profiles)
  initStyleComparisonChart() {
    const ctx = this.styleComparisonTarget.getContext('2d')
    const data = this.comparisonDataValue

    // Extract styles and comparison data (team average or ideal profile)
    const managerStyles = data.manager || {
      directive: 0,
      supportive: 0,
      participative: 0,
      achievement_oriented: 0
    }

    const comparisonStyles = data.comparison || {
      directive: 0,
      supportive: 0,
      participative: 0,
      achievement_oriented: 0
    }

    this.styleComparisonChart = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: [
          'Directive',
          'Supportive',
          'Participative',
          'Achievement-Oriented'
        ],
        datasets: [
          {
            label: 'Your Style',
            data: [
              managerStyles.directive || 0,
              managerStyles.supportive || 0,
              managerStyles.participative || 0,
              managerStyles.achievement_oriented || 0
            ],
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(54, 162, 235, 1)'
          },
          {
            label: data.comparison_label || 'Comparison',
            data: [
              comparisonStyles.directive || 0,
              comparisonStyles.supportive || 0,
              comparisonStyles.participative || 0,
              comparisonStyles.achievement_oriented || 0
            ],
            backgroundColor: 'rgba(255, 99, 132, 0.2)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(255, 99, 132, 1)'
          }
        ]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100
          }
        },
        plugins: {
          datalabels: {
            display: false
          }
        }
      }
    })
  }

  // Effectiveness Chart (spider/radar chart showing effectiveness across scenarios)
  initEffectivenessChart() {
    const ctx = this.effectivenessChartTarget.getContext('2d')
    const data = this.effectivenessDataValue || {}
    
    // Default scenarios if not provided
    const scenarios = data.scenarios || [
      'New Team Members',
      'Crisis Situations',
      'Routine Tasks',
      'Creative Tasks',
      'Experienced Team',
      'High Pressure'
    ]
    
    // Default effectiveness scores (middle range)
    const defaultEffectiveness = scenarios.map(() => 50)
    
    this.effectivenessChart = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: scenarios,
        datasets: [{
          label: 'Leadership Effectiveness',
          data: data.scores || defaultEffectiveness,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2,
          pointBackgroundColor: 'rgba(75, 192, 192, 1)',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(75, 192, 192, 1)'
        }]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20
            }
          }
        },
        plugins: {
          datalabels: {
            backgroundColor: function(context) {
              return context.dataset.backgroundColor;
            },
            borderRadius: 4,
            color: 'white',
            font: {
              weight: 'bold'
            },
            formatter: Math.round,
            padding: 6
          }
        }
      }
    })
  }

  // Situational Adaptability Chart (stacked bar chart)
  initSituationalAdaptabilityChart() {
    const ctx = this.situationalAdaptabilityTarget.getContext('2d')
    const data = this.adaptabilityDataValue || {}
    
    // Default situations if not provided
    const situations = data.situations || [
      'Team Onboarding',
      'Performance Issues',
      'Strategic Planning',
      'Daily Operations',
      'Crisis Management',
      'Team Development'
    ]
    
    // Get style data or use defaults
    const directive = data.directive || situations.map(() => 25)
    const supportive = data.supportive || situations.map(() => 25)
    const participative = data.participative || situations.map(() => 25)
    const achievementOriented = data.achievement_oriented || situations.map(() => 25)
    
    this.adaptabilityChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: situations,
        datasets: [
          {
            label: 'Directive',
            data: directive,
            backgroundColor: 'rgba(255, 99, 132, 0.8)'
          },
          {
            label: 'Supportive',
            data: supportive,
            backgroundColor: 'rgba(54, 162, 235, 0.8)'
          },
          {
            label: 'Participative',
            data: participative,
            backgroundColor: 'rgba(75, 192, 192, 0.8)'
          },
          {
            label: 'Achievement-Oriented',
            data: achievementOriented,
            backgroundColor: 'rgba(255, 206, 86, 0.8)'
          }
        ]
      },
      options: {
        plugins: {
          datalabels: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.dataset.label || '';
                return label + ': ' + context.parsed.y + '%';
              }
            }
          }
        },
        responsive: true,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        }
      }
    })
  }

  // Team Compatibility Chart (polar area chart)
  initTeamCompatibilityChart() {
    const ctx = this.teamCompatibilityTarget.getContext('2d')
    const data = this.teamDataValue || {}
    
    // Default compatibility data
    const teamMembers = data.members || [
      'Team Member 1',
      'Team Member 2',
      'Team Member 3',
      'Team Member 4',
      'Team Member 5'
    ]
    
    const compatibilityScores = data.scores || teamMembers.map(() => 70)
    const colors = teamMembers.map((_, index) => {
      const hue = (index * 30) % 360
      return `hsla(${hue}, 70%, 60%, 0.7)`
    })
    const borderColors = teamMembers.map((_, index) => {
      const hue = (index * 30) % 360
      return `hsla(${hue}, 70%, 50%, 1)`
    })
    
    this.teamCompatibilityChart = new Chart(ctx, {
      type: 'polarArea',
      data: {
        labels: teamMembers,
        datasets: [{
          data: compatibilityScores,
          backgroundColor: colors,
          borderColor: borderColors,
          borderWidth: 1
        }]
      },
      options: {
        plugins: {
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold'
            },
            formatter: function(value) {
              return value + '%'
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return 'Compatibility: ' + context.parsed.r + '%';
              }
            }
          }
        },
        scales: {
          r: {
            max: 100,
            min: 0,
            ticks: {
              stepSize: 20
            }
          }
        }
      }
    })
  }

  // Cleanup
  disconnect() {
    if (this.styleComparisonChart) {
      this.styleComparisonChart.destroy()
    }
    if (this.effectivenessChart) {
      this.effectivenessChart.destroy()
    }
    if (this.adaptabilityChart) {
      this.adaptabilityChart.destroy()
    }
    if (this.teamCompatibilityChart) {
      this.teamCompatibilityChart.destroy()
    }
  }
}